import { StyleSheet, css } from 'aphrodite'
import type { StyleDeclaration } from 'aphrodite'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import { useOffline } from '../contexts/OfflineContext'

export interface AddressComponents {
  street_address: string
  city: string
  state: string
  zip_code: string
}

type GooglePlaceOption = {
  label: string
  value: string
  addressComponents?: AddressComponents
} | null

interface GooglePlacesSelectProps {
  value: GooglePlaceOption
  onChange: (option: GooglePlaceOption) => void
  placeholder: string
  isDisabled?: boolean
  className?: StyleDeclaration
}

// Parse Google Places address_components into structured fields
const parseAddressComponents = (
  components: google.maps.GeocoderAddressComponent[],
): AddressComponents => {
  let streetNumber = ''
  let route = ''
  let city = ''
  let state = ''
  let zipCode = ''
  let zipSuffix = ''

  for (const component of components) {
    const types = component.types
    if (types.includes('street_number')) {
      streetNumber = component.long_name
    } else if (types.includes('route')) {
      route = component.long_name
    } else if (
      types.includes('locality') ||
      types.includes('sublocality') ||
      types.includes('sublocality_level_1')
    ) {
      city = component.long_name
    } else if (types.includes('administrative_area_level_1')) {
      state = component.short_name // Use short_name for state abbreviation
    } else if (types.includes('postal_code')) {
      zipCode = component.long_name
    } else if (types.includes('postal_code_suffix')) {
      zipSuffix = component.long_name
    }
  }

  // Combine zip code with suffix if available (zip+4 format)
  const fullZipCode = zipSuffix ? `${zipCode}-${zipSuffix}` : zipCode

  return {
    street_address: streetNumber && route ? `${streetNumber} ${route}` : route,
    city,
    state,
    zip_code: fullZipCode,
  }
}

const GooglePlacesSelect: React.FC<GooglePlacesSelectProps> = ({
  value,
  onChange,
  placeholder,
  isDisabled = false,
  className,
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [autocomplete, setAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null)
  const { isOffline } = useOffline()

  useEffect(() => {
    if (!inputRef.current) return

    const options = {
      types: ['address'],
      fields: ['formatted_address', 'address_components'],
    }

    const autocompleteInstance = new google.maps.places.Autocomplete(
      inputRef.current,
      options,
    )
    setAutocomplete(autocompleteInstance)

    // Add CSS to ensure dropdown stays positioned correctly
    const style = document.createElement('style')
    style.id = 'google-places-fix'
    style.textContent = `
      .pac-container {
        z-index: 99999 !important;
        position: absolute !important;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
        border: 1px solid #D1D5DB !important;
        border-radius: 6px !important;
        background: white !important;
        margin-top: 2px !important;
      }
      .pac-item {
        cursor: pointer !important;
        padding: 8px 12px !important;
        border-bottom: 1px solid #F3F4F6 !important;
        font-size: 14px !important;
      }
      .pac-item:hover {
        background-color: #F3F4F6 !important;
      }
      .pac-item:last-child {
        border-bottom: none !important;
      }
    `

    // Only add if not already present
    if (!document.getElementById('google-places-fix')) {
      document.head.appendChild(style)
    }

    return () => {
      google.maps.event.clearInstanceListeners(autocompleteInstance)
      // Clean up style if this is the last instance
      const existingStyle = document.getElementById('google-places-fix')
      existingStyle?.parentNode?.removeChild(existingStyle)
    }
  }, [])

  useEffect(() => {
    if (!autocomplete) return

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace()
      if (place.formatted_address) {
        const addressComponents = place.address_components
          ? parseAddressComponents(place.address_components)
          : undefined
        onChange({
          label: place.formatted_address,
          value: place.formatted_address,
          addressComponents,
        })
      }
    })

    // No additional handling needed - CSS takes care of styling
  }, [autocomplete, onChange])

  // Handle focus to ensure dropdown appears correctly
  const handleFocus = () => {
    // Let Google handle the positioning naturally
  }

  return (
    <div className={css(styles.container)}>
      <input
        ref={inputRef}
        type="text"
        placeholder={isOffline ? 'Type address manually' : placeholder}
        value={value?.label || ''}
        onChange={(e) => {
          if (!isDisabled) {
            if (!e.target.value) {
              onChange(null)
            } else {
              onChange({ label: e.target.value, value: e.target.value })
            }
          }
        }}
        onFocus={handleFocus}
        disabled={isDisabled}
        className={css(styles.input, className)}
      />
      {value && !isDisabled && (
        <button
          className={css(styles.clearButton)}
          onClick={() => onChange(null)}
          type="button"
        >
          ×
        </button>
      )}
      {isOffline && (
        <div className={css(styles.offlineHint)}>
          <i className="fa-solid fa-wifi-slash" style={{ marginRight: 4 }} />
          Address suggestions unavailable offline. You can type the address
          manually.
        </div>
      )}
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    position: 'relative',
    width: '100%',
    zIndex: 1,
  },
  input: {
    width: '100%',
    padding: '8px 12px',
    fontSize: '14px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    outline: 'none',
    ':focus': {
      borderColor: '#3B82F6',
      boxShadow: '0 0 0 1px #3B82F6',
    },
  },
  clearButton: {
    position: 'absolute',
    right: '8px',
    top: '50%',
    transform: 'translateY(-52%)',
    background: 'none',
    border: 'none',
    fontSize: '18px',
    cursor: 'pointer',
    color: '#9CA3AF',
    padding: '4px 8px',
    ':hover': {
      color: '#6B7280',
    },
  },
  offlineHint: {
    marginTop: '4px',
    fontSize: '12px',
    color: '#92400E',
    backgroundColor: '#FEF3C7',
    padding: '6px 10px',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
  },
})

export default GooglePlacesSelect
